import React, { useEffect, useState } from "react";
import {
  CustomPhoneInput,
  Input,
  Password,
  RadioGroup,
} from "../../../Components/FormElements";
import { PrimaryButton } from "../../../Components/Cta";
import { useNavigate } from "react-router-dom";

export default function Stage1({ setTitle, updateData, changePage }) {
  const navigate = useNavigate();
  useEffect(() => {
    setTitle("Basic details");
  }, [setTitle]);

  // Values
  const [fullName, setFullName] = useState("");
  const [selectedOption, setSelectedOption] = useState("phoneNumber");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Errors (messages) + touched
  const [errors, setErrors] = useState({
    fullName: "",
    phone: "",
    email: "",
    password: "",
  });
  const [touched, setTouched] = useState({
    fullName: false,
    phone: false,
    email: false,
    password: false,
  });

  const radioOptions = [
    { label: "Phone Number", value: "phoneNumber" },
    { label: "Email", value: "email" },
  ];

  const handleRadioChange = (value) => {
    setSelectedOption(value);
    // When user switches input type clear the other field's error and touched state
    setErrors((prev) => ({ ...prev, phone: "", email: "" }));
    setTouched((t) => ({ ...t, phone: false, email: false }));
  };

  // Safely get value from event or direct value
  const getVal = (v) => (v && v.target ? v.target.value : v ?? "");

  // Central validation - validates all fields but uses selectedOption to decide
  // whether phone or email is required.
  const validate = (vals, selOption = selectedOption) => {
    const next = { fullName: "", phone: "", email: "", password: "" };

    // Full name
    if (!vals.fullName.trim()) next.fullName = "Full name is required";

    // Phone (require 10+ digits) - only when phone is selected
    const digits = (vals.phone || "").replace(/\D/g, "");
    if (selOption === "phoneNumber") {
      if (!digits) next.phone = "Phone number is required";
      else if (digits.length < 10) next.phone = "Enter a valid phone number";
    }

    // Email (basic format) - only when email is selected
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (selOption === "email") {
      if (!vals.email || !vals.email.trim()) next.email = "Email is required";
      else if (!emailRegex.test(vals.email))
        next.email = "Please provide a valid email";
    }

    // Password (min 6)
    if (!vals.password.trim()) next.password = "Password is required";
    else if (vals.password.length < 6)
      next.password = "Password must be at least 6 characters";

    return next;
  };

  // Only allow alphabets + spaces
  const nameRegex = /^[A-Za-z\s]*$/;

  const onChangeFullName = (v) => {
    const val = getVal(v);

    // Block invalid characters
    if (!nameRegex.test(val)) return;
    if (val.length >= 100) return;
    setFullName(val);

    if (touched.fullName) {
      setErrors((prev) => ({
        ...prev,
        fullName: validate({ fullName: val, phone, email, password }).fullName,
      }));
    }
  };

  const onChangePhone = (v) => {
    const val = getVal(v);
    setPhone(val);
    if (touched.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: validate({ fullName, phone: val, email, password }).phone,
      }));
    }
    // Clear phone error while typing for better UX
    if (errors.phone) setErrors((p) => ({ ...p, phone: "" }));
  };

  const onChangeEmail = (v) => {
    const val = getVal(v);
    setEmail(val);
    if (touched.email) {
      setErrors((prev) => ({
        ...prev,
        email: validate({ fullName, phone, email: val, password }, "email")
          .email,
      }));
    }
    if (errors.email) setErrors((p) => ({ ...p, email: "" }));
  };

  const onChangePassword = (v) => {
    const val = getVal(v);
    if (val.length >= 13) return;
    setPassword(val);
    if (touched.password) {
      setErrors((prev) => ({
        ...prev,
        password: validate({ fullName, phone, email, password: val }).password,
      }));
    }
  };

  const _handleSubmit = () => {
    // mark all touched for the fields that matter
    setTouched((t) => ({
      ...t,
      fullName: true,
      password: true,
      phone: selectedOption === "phoneNumber" ? true : t.phone,
      email: selectedOption === "email" ? true : t.email,
    }));

    const nextErrors = validate(
      { fullName, phone, email, password },
      selectedOption
    );
    setErrors(nextErrors);

    // stop if any error message is non-empty for required fields
    const hasError =
      nextErrors.fullName ||
      nextErrors.password ||
      (selectedOption === "phoneNumber" ? nextErrors.phone : nextErrors.email);

    if (hasError) return;

    // pass only the relevant contact field but include both for downstream flexibility
    updateData({ fullName, selectedOption, phone, email, password });
    changePage(2);
  };

  return (
    <div className="row row-gap-4">
      <div className="col-12 d-flex flex-column">
        <div className=" p-3 d-flex flex-column row-gap-3 mt-1 e-bg-ghost-white-dark e-border-radius-16 e-border-1px-pale-lavender">
          {/* Full Name */}
          <Input
            require
            label="Full name"
            labelStyle="e-font-16 e-font-weight-400 e-primary-font-poppins pb-2"
            placeholder="Enter full name"
            className="w-100 pe-lg-5"
            type="text"
            value={fullName}
            error={errors.fullName}
            handleChange={onChangeFullName}
            onBlur={() => {
              setTouched((t) => ({ ...t, fullName: true }));
              setErrors((prev) => ({
                ...prev,
                fullName: validate({ fullName, phone, email, password })
                  .fullName,
              }));
            }}
          />

          {/* Radio Group */}
          <RadioGroup
            type={2}
            require={true}
            data={radioOptions}
            selectedItem={selectedOption}
            onSelectItem={handleRadioChange}
          />

          {/* Phone Input or Email Input depending on selection */}
          {selectedOption === "phoneNumber" ? (
            <CustomPhoneInput
              placeholder="Enter your phone number"
              label="Phone Number"
              labelClass="e-font-16 e-font-weight-400"
              require={true}
              value={phone}
              error={errors.phone}
              handleChange={onChangePhone}
              onEnterKeyPress={_handleSubmit}
              onBlur={() => {
                setTouched((t) => ({ ...t, phone: true }));
                setErrors((prev) => ({
                  ...prev,
                  phone: validate({ fullName, phone, email, password }).phone,
                }));
              }}
            />
          ) : (
            <Input
              id="email"
              type="email"
              label="Email"
              placeholder="Enter your email address"
              require={true}
              value={email}
              error={errors.email}
              className="e-primary-font-poppins w-100"
              handleChange={onChangeEmail}
              onBlur={() => {
                setTouched((t) => ({ ...t, email: true }));
                setErrors((prev) => ({
                  ...prev,
                  email: validate({ fullName, phone, email, password }, "email")
                    .email,
                }));
              }}
            />
          )}

          {/* Password */}
          <Password
            require
            label="Temporary Password"
            labelStyle="e-font-16 e-font-weight-400 e-primary-font-poppins pb-2"
            placeholder="Enter password"
            className="w-100 pe-lg-5"
            value={password}
            error={errors.password}
            handleChange={onChangePassword}
            onBlur={() => {
              setTouched((t) => ({ ...t, password: true }));
              setErrors((prev) => ({
                ...prev,
                password: validate({ fullName, phone, email, password })
                  .password,
              }));
            }}
          />
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="col-lg-10 offset-lg-2 col-12">
        <div className="row">
          <div className="col-6">
            <PrimaryButton
              ClassName="e-border-radius-pill e-border-1px-blue py-3 e-font-weight-500 e-font-16 w-100 bg-transparent e-text-blue-violet e-primary-font-poppins"
              name="Cancel"
              onPress={() => navigate("/customer")}
            />
          </div>
          <div className="col-6">
            <PrimaryButton
              ClassName="e-border-radius-pill e-font-weight-500 py-3 e-font-16 w-100 e-bg-blue-violet text-white e-primary-font-poppins"
              name="Continue"
              onPress={_handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
